import { useId } from "react";
import {
	Area,
	AreaChart,
	CartesianGrid,
	ReferenceDot,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import type {
	ChartPoint,
	Metric,
	TimeRange,
} from "../hooks/use-portfolio-chart";
import {
	formatAxisDate,
	formatTooltipDate,
} from "../lib/portfolio-chart-format";
import { createEdgeSafeLabel } from "./portfolio-chart-edge-label";
import {
	formatCompactNumber,
	formatSignedUsd,
	formatUsd,
} from "#/lib/formatters";

type Props = {
	series: ChartPoint[];
	max: ChartPoint | null;
	min: ChartPoint | null;
	timeRange: TimeRange;
	metric: Metric;
	metricLabel: string;
	gradientOffset: number;
};

export function PortfolioChartArea({
	series,
	max,
	min,
	timeRange,
	metric,
	metricLabel,
	gradientOffset,
}: Props) {
	const gradientId = useId();
	const maxColor =
		max?.v > 0
			? "var(--trade-green)"
			: max?.v < 0
				? "var(--trade-red)"
				: "var(--muted-foreground)";
	const minColor =
		min?.v > 0
			? "var(--trade-green)"
			: min?.v < 0
				? "var(--trade-red)"
				: "var(--muted-foreground)";

	return (
		<ResponsiveContainer width="100%" height="100%">
			<AreaChart
				data={series}
				margin={{ top: 24, right: 12, left: 0, bottom: 4 }}
			>
				<defs>
					<linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
						<stop
							offset="0%"
							stopColor="var(--trade-green)"
							stopOpacity={0.35}
						/>
						<stop
							offset={`${gradientOffset * 100}%`}
							stopColor="var(--trade-green)"
							stopOpacity={0.05}
						/>
						<stop
							offset={`${gradientOffset * 100}%`}
							stopColor="var(--trade-red)"
							stopOpacity={0.05}
						/>
						<stop
							offset="100%"
							stopColor="var(--trade-red)"
							stopOpacity={0.35}
						/>
					</linearGradient>
					<linearGradient
						id={`${gradientId}-stroke`}
						x1="0"
						y1="0"
						x2="0"
						y2="1"
					>
						<stop offset="0%" stopColor="var(--trade-green)" />
						<stop
							offset={`${gradientOffset * 100}%`}
							stopColor="var(--trade-green)"
						/>
						<stop
							offset={`${gradientOffset * 100}%`}
							stopColor="var(--trade-red)"
						/>
						<stop offset="100%" stopColor="var(--trade-red)" />
					</linearGradient>
				</defs>
				<CartesianGrid
					strokeDasharray="3 3"
					stroke="var(--border)"
					vertical={false}
					opacity={0.4}
				/>
				<XAxis
					dataKey="t"
					type="number"
					scale="time"
					domain={["dataMin", "dataMax"]}
					tickFormatter={(ts: number) => formatAxisDate(ts, timeRange)}
					tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
					axisLine={false}
					tickLine={false}
					minTickGap={40}
				/>
				<YAxis
					orientation="right"
					tickFormatter={(v: number) => `$${formatCompactNumber(v)}`}
					tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
					axisLine={false}
					tickLine={false}
					width={48}
				/>
				{metric === "pnl" && (
					<ReferenceLine
						y={0}
						stroke="var(--muted-foreground)"
						strokeDasharray="4 4"
						strokeOpacity={0.6}
					/>
				)}
				<Tooltip
					contentStyle={{
						background: "var(--popover)",
						border: "1px solid var(--border)",
						borderRadius: 8,
						fontSize: 12,
						color: "var(--foreground)",
					}}
					labelFormatter={(label) =>
						typeof label === "number"
							? formatTooltipDate(label)
							: String(label ?? "")
					}
					formatter={(value) => [
						metric === "pnl"
							? formatSignedUsd(value as number)
							: formatUsd(value as number),
						metricLabel,
					]}
				/>
				<Area
					type="monotone"
					dataKey="v"
					stroke={`url(#${gradientId}-stroke)`}
					strokeWidth={2}
					fill={`url(#${gradientId})`}
					isAnimationActive={false}
				/>
				{max && series.length > 1 && (
					<ReferenceDot
						x={max.t}
						y={max.v}
						r={3}
						fill={maxColor}
						stroke="var(--card)"
						strokeWidth={2}
						ifOverflow="extendDomain"
						label={createEdgeSafeLabel({
							text: formatSignedUsd(max.v),
							pointTime: max.t,
							series,
							side: "top",
							fill: maxColor,
						})}
					/>
				)}
				{min && series.length > 1 && min.t !== max?.t && (
					<ReferenceDot
						x={min.t}
						y={min.v}
						r={3}
						fill={minColor}
						stroke="var(--card)"
						strokeWidth={2}
						ifOverflow="extendDomain"
						label={createEdgeSafeLabel({
							text: formatSignedUsd(min.v),
							pointTime: min.t,
							series,
							side: "bottom",
							fill: minColor,
						})}
					/>
				)}
			</AreaChart>
		</ResponsiveContainer>
	);
}
